import { ENTITY_ATTRIBUTE_DEFS } from '@mariana/labos-client';
import type { EntityType } from '../types';
import type { ColumnDef } from '../config/viewsConfig';

/**
 * Resolve a non-FK column key against an entity's static schema, mirroring how
 * ColumnPicker builds columns for that entity:
 *  - a direct attribute → `{ key, label, render }` (date/json render from type)
 *  - a discovered JSON leaf (`{jsonAttr}_{leaf}`) → an extract column
 * Returns undefined when the key matches neither.
 */
function resolveLocalColumnDef(
  key: string,
  entityType: EntityType,
): ColumnDef | undefined {
  const defs = ENTITY_ATTRIBUTE_DEFS[entityType];
  if (!defs) return undefined;

  const direct = defs.find(d => d.name === key);
  if (direct) {
    const render: ColumnDef['render'] =
      direct.data_type === 'date' ? 'date' : direct.data_type === 'json' ? 'json' : undefined;
    return { key, label: key, render };
  }

  // Discovered JSON extract columns (`{jsonAttr}_{leaf}`). Sort by length desc
  // so e.g. `raw_worksheet_data` wins over `worksheet_data` for shared suffixes.
  const jsonNames = defs.filter(d => d.data_type === 'json').map(d => d.name);
  const sorted = [...jsonNames].sort((a, b) => b.length - a.length);
  for (const ja of sorted) {
    const prefix = `${ja}_`;
    if (key.startsWith(prefix)) {
      const leaf = key.slice(prefix.length);
      if (!leaf) continue;
      return {
        key,
        label: `${ja}.${leaf}`,
        extract: { from: ja, path: leaf },
        sortable: false,
      };
    }
  }
  return undefined;
}

/**
 * Synthesize a ColumnDef for a foreign-key column key (`fkAttr.targetKey`),
 * mirroring how ColumnPicker builds FK columns during attribute discovery.
 * Handles both plain FK attributes (`sample_id.date_created`) and FK JSON
 * extract leaves (`sample_id.pilot_data_foo`). Returns undefined if the key
 * isn't a resolvable single-hop FK reference.
 */
function resolveFkColumnDef(
  key: string,
  entityType: EntityType,
): ColumnDef | undefined {
  const dotIdx = key.indexOf('.');
  if (dotIdx <= 0) return undefined;

  const fkAttrName = key.slice(0, dotIdx);
  const remainder = key.slice(dotIdx + 1);
  if (!remainder) return undefined;

  const defs = ENTITY_ATTRIBUTE_DEFS[entityType];
  const fkDef = defs?.find(d => d.name === fkAttrName && d.referenced_entity_type);
  if (!fkDef?.referenced_entity_type) return undefined;

  const inner = resolveLocalColumnDef(remainder, fkDef.referenced_entity_type);
  if (!inner) return undefined;

  return { ...inner, key };
}

/**
 * Map a column key (from a shared URL or localStorage) back to a ColumnDef.
 * Resolution order: explicitly-known columns, then FK columns, then direct /
 * JSON-extract columns synthesized from the entity's static schema. This is the
 * inverse of the columns ColumnPicker can produce, so any picked column can be
 * restored.
 */
export function resolveColumnDefForKey(
  key: string,
  entityType: EntityType,
  allColumns: ColumnDef[],
): ColumnDef | undefined {
  const known = allColumns.find(c => c.key === key);
  if (known) return known;

  const fkCol = resolveFkColumnDef(key, entityType);
  if (fkCol) return fkCol;

  return resolveLocalColumnDef(key, entityType);
}

/** Restore column order from URL or localStorage key list. */
export function resolveColumnOrderFromKeys(
  keys: string[],
  entityType: EntityType,
  allColumns: ColumnDef[],
): ColumnDef[] {
  const out: ColumnDef[] = [];
  for (const k of keys) {
    const trimmed = k.trim();
    if (!trimmed) continue;
    const col = resolveColumnDefForKey(trimmed, entityType, allColumns);
    if (col) out.push(col);
  }
  return out;
}
